import invariant from "tiny-invariant";

import type { Instruction } from "@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item";

export type TreeItem = {
  id: string;
  title?: string;
  path?: string | null;
  // type?: "PAI" | "FILHO";
  // isDraft?: boolean;
  type?: "PAI" | "FILHO";
  children: TreeItem[];
  editadoEm?: Date | null;
  isOpen?: boolean;
  position: string | null;
  status?: number;
};

export type TreeState = {
  lastAction: TreeAction | null;
  data: TreeItem[];
};

export function getInitialTreeState(): TreeState {
  return { data: getInitialData(), lastAction: null };
}

export function getInitialData(): TreeItem[] {
  return [
    // {
    //   id: "1",
    //   isOpen: true,
    //   title: "Diretoria",
    //   children: [
    //     {
    //       id: "1.3",
    //       isOpen: true,
    //       title: "Tela de Diretoria",
    //       children: [
    //         {
    //           id: "1.3.1",
    //           title: "Flho",
    //           children: [],
    //         },
    //       ],
    //     },
    //     {
    //       id: "1.4",
    //       title: "Clientes e Contratos",
    //       isDraft: true,
    //       children: [],
    //     },
    //   ],
    // },
    // {
    //   id: "2",
    //   title: "Operação",
    //   isOpen: true,
    //   children: [
    //     {
    //       id: "2.3",
    //       isOpen: true,
    //       title: "Ocorrências",
    //       children: [
    //         {
    //           id: "2.3.1",
    //           title: "Operacional",
    //           children: [],
    //         },
    //         {
    //           id: "2.3.2",
    //           title: "Relatórios",
    //           children: [],
    //         },
    //       ],
    //     },
    //   ],
    // },
  ];
}

export type TreeAction =
  | {
      type: "instruction";
      instruction: Instruction;
      itemId: string;
      targetId: string;
    }
  | {
      type: "toggle";
      itemId: string;
    }
  | {
      type: "expand";
      itemId: string;
    }
  | {
      type: "collapse";
      itemId: string;
    }
  | { type: "modal-move"; itemId: string; targetId: string }
  | { type: "remove"; itemId: string }
  | { type: "rename"; itemId: string; newTitle: string; newPath: string }
  | { type: "add-item-from-tree"; itemId: string; position: string }
  | {
      type: "remove-item-from-tree";
      itemId: string;
    }
  | {
      type: "create-child";
      itemId: string;
      parentId: string;
      newItem: TreeItem;
    }
  | { type: "create"; itemId: string; newItem: TreeItem }
  | { type: "move"; itemId: string; targetId: string; index: number }
  | { type: "status"; itemId: string; status: number; editadoEm?: Date }
  | { type: "reset"; data: TreeItem[] };

export const tree = {
  remove(data: TreeItem[], id: string): TreeItem[] {
    return data
      .filter((item) => item.id !== id)
      .map((item) => {
        if (tree.hasChildren(item)) {
          return {
            ...item,
            children: tree.remove(item.children, id),
          };
        }
        return item;
      });
  },
  deleteItem(data: TreeItem[], id: string, item: TreeItem): TreeItem[] {
    let newData = data;
    if (tree.hasChildren(item)) {
      newData = [...data, ...tree.findPages(item)];
    }
    // return newData.filter((item) => item.id !== id);
    return tree.remove(newData, id);
  },
  create(data: TreeItem[], newItem: TreeItem): TreeItem[] {
    return [...data, newItem];
  },
  updateStatus(
    data: TreeItem[],
    itemId: string,
    status: number,
    editadoEm?: Date
  ): TreeItem[] {
    return data.flatMap((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          status,
          ...(editadoEm ? { editadoEm } : {}),
          ...(status === 0 ? { position: null } : {}),
        };
      }
      if (!tree.hasChildren(item)) {
        return item;
      }
      return {
        ...item,
        children: tree.updateStatus(item.children, itemId, status, editadoEm),
      };
    });
  },

  insertBefore(
    data: TreeItem[],
    newItem: TreeItem,
    targetId?: string
  ): TreeItem[] {
    if (!targetId) return [newItem, ...data];
    return data.flatMap((item) => {
      if (item.id === targetId) {
        return [newItem, item];
      }
      if (tree.hasChildren(item)) {
        return {
          ...item,
          children: tree.insertBefore(item.children, newItem, targetId),
        };
      }
      return item;
    });
  },
  insertAfter(
    data: TreeItem[],
    newItem: TreeItem,
    targetId?: string
  ): TreeItem[] {
    if (!targetId) return [...data, newItem];
    return data.flatMap((item) => {
      if (item.id === targetId) {
        return [item, newItem];
      }

      if (tree.hasChildren(item)) {
        return {
          ...item,
          children: tree.insertAfter(item.children, newItem, targetId),
        };
      }

      return item;
    });
  },
  insertChild(
    data: TreeItem[],
    targetId: string,
    newItem: TreeItem
  ): TreeItem[] {
    return data.flatMap((item) => {
      if (item.id === targetId) {
        // already a parent: add as first child
        return {
          ...item,
          // opening item so you can see where item landed
          isOpen: true,
          children: [...item.children, newItem],
        };
      }

      if (!tree.hasChildren(item)) {
        return item;
      }

      return {
        ...item,
        children: tree.insertChild(item.children, targetId, newItem),
      };
    });
  },

  addPosition(data: TreeItem[], itemId: string, position: string): TreeItem[] {
    let lastPosition = "1";
    // data.forEach((item) => {
    //   if (item.position) {
    //     const position = parseInt(item.position, 10);
    //     if (position > lastPosition) {
    //       lastPosition = position;
    //     }
    //   }
    // });
    return data.flatMap((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          position: position,
        };
      }
      if (!tree.hasChildren(item)) {
        return item;
      }
      return {
        ...item,
        children: tree.addPosition(item.children, itemId, position),
      };
    });
  },
  removePosition(data: TreeItem[], itemId: string): TreeItem[] {
    return data.flatMap((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          position: null, // remove position
        };
      }

      if (!tree.hasChildren(item)) {
        return item;
      }

      return {
        ...item,
        children: tree.removePosition(item.children, itemId),
      };
    });
  },
  find(data: TreeItem[], itemId: string): TreeItem | undefined {
    for (const item of data) {
      if (item.id === itemId) {
        return item;
      }

      if (tree.hasChildren(item)) {
        const result = tree.find(item.children, itemId);
        if (result) {
          return result;
        }
      }
    }
  },
  getPathToItem({
    current,
    targetId,
    parentIds = [],
  }: {
    current: TreeItem[];
    targetId: string;
    parentIds?: string[];
  }): string[] | undefined {
    for (const item of current) {
      if (item.id === targetId) {
        return parentIds;
      }
      const nested = tree.getPathToItem({
        current: item.children,
        targetId: targetId,
        parentIds: [...parentIds, item.id],
      });
      if (nested) {
        return nested;
      }
    }
  },
  rename(
    data: TreeItem[],
    itemId: string,
    title: string,
    path: string
  ): TreeItem[] {
    return data.flatMap((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          title,
          path,
        };
      }
      if (!tree.hasChildren(item)) {
        return item;
      }
      return {
        ...item,
        children: tree.rename(item.children, itemId, title, path),
      };
    });
  },
  hasChildren(item: TreeItem): boolean {
    return item.children.length > 0;
  },
  findPages(item: TreeItem): TreeItem[] {
    return item.children.reduce((acc: TreeItem[], child: TreeItem) => {
      if (child.type === "FILHO") {
        return [...acc, { ...child, status: 0, position: null }];
      }
      if (tree.hasChildren(child)) {
        return [...acc, ...tree.findPages(child)];
      }
      return acc;
    }, []);
  },
};

export function treeStateReducer(
  state: TreeState,
  action: TreeAction
): TreeState {
  if (action.type === "reset") {
    return {
      data: action.data,
      lastAction: action,
    };
  }
  return {
    data: dataReducer(state.data, action),
    lastAction: action,
  };
}

const dataReducer = (data: TreeItem[], action: TreeAction) => {
  // console.log("action", action);
  if (action.type === "reset") {
    return action.data;
  }
  if (action.type === "create") {
    const newItem = action.newItem;
    if (newItem.id === action.itemId) {
      return tree.insertAfter(data, newItem);
    }
    return data;
  }
  if (action.type === "create-child") {
    const newItem = action.newItem;
    if (newItem.id === action.itemId) {
      return tree.insertChild(data, action.parentId, newItem);
    }
    return data;
  }

  const item = tree.find(data, action.itemId);
  if (!item) {
    return data;
  }
  if (action.type === "remove") {
    return tree.deleteItem(data, action.itemId, item);
    // return tree.deleteItem(data, action.itemId, item);
  }
  if (action.type === "add-item-from-tree") {
    if (item.id === action.itemId) {
      return tree.addPosition(data, action.itemId, action.position);
    }
    return data;
  }
  if (action.type === "remove-item-from-tree") {
    if (item.id === action.itemId) {
      let result = tree.remove(data, action.itemId);
      result = tree.insertAfter(result, item);
      result = tree.removePosition(result, item.id);
      return result;
    }
    return data;
  }
  if (action.type === "status") {
    if (item.id === action.itemId) {
      let result = tree.remove(data, action.itemId);
      result = tree.insertAfter(result, item);
      result = tree.updateStatus(
        result,
        action.itemId,
        action.status,
        action.editadoEm
      );
      return result;
    }
  }

  if (action.type === "instruction") {
    const instruction = action.instruction;

    if (instruction.type === "reparent") {
      const path = tree.getPathToItem({
        current: data,
        targetId: action.targetId,
      });
      invariant(path);
      const desiredId = path[instruction.desiredLevel];
      let result = tree.remove(data, action.itemId);
      result = tree.insertAfter(result, item, desiredId);
      return result;
    }

    // the rest of the actions require you to drop on something else
    if (action.itemId === action.targetId) {
      return data;
    }

    // if (instruction.type === "reorder-above") {
    // let result = tree.remove(data, action.itemId);
    // result = tree.insertBefore(result, item, action.targetId);
    // return result;
    // }

    // if (instruction.type === "reorder-below") {
    // let result = tree.remove(data, action.itemId);
    // result = tree.insertAfter(result, item, action.targetId);
    // return result;
    // }

    if (instruction.type === "make-child") {
      let result = tree.remove(data, action.itemId);
      result = tree.insertChild(result, action.targetId, item);
      return result;
    }

    console.warn("TODO: action not implemented", instruction);

    return data;
  }

  function toggle(item: TreeItem): TreeItem {
    if (!tree.hasChildren(item) || action.type === "reset") {
      return item;
    }

    if (item.id === action.itemId) {
      return { ...item, isOpen: !item.isOpen };
    }

    return { ...item, children: item.children.map(toggle) };
  }

  if (action.type === "toggle") {
    return data.map(toggle);
  }

  if (action.type === "expand") {
    if (tree.hasChildren(item) && !item.isOpen) {
      return data.map(toggle);
    }
    return data;
  }

  if (action.type === "collapse") {
    if (tree.hasChildren(item) && item.isOpen) {
      return data.map(toggle);
    }
    return data;
  }
  if (action.type === "rename") {
    if (item.id === action.itemId) {
      // return data.map((i) =>
      //   i.id === item.id ? { ...i, title: action.newTitle } : i
      // );
      return tree.rename(
        data,
        action.itemId,
        action.newTitle,
        action.newPath || ""
      );
    }
    return data;
  }
  if (action.type === "modal-move") {
    let result = tree.remove(data, item.id);

    const siblingItems = getChildItems(result, action.targetId);

    if (siblingItems.length === 0) {
      if (action.targetId === "") {
        /**
         * If the target is the root level, and there are no siblings, then
         * the item is the only thing in the root level.
         */
        result = [item];
      } else {
        /**
         * Otherwise for deeper levels that have no children, we need to
         * use `insertChild` instead of inserting relative to a sibling.
         */
        result = tree.insertChild(result, action.targetId, item);
      }
    } else {
      //  else if (action.index === siblingItems.length) {
      const relativeTo = siblingItems[siblingItems.length - 1];
      /**
       * If the position selected is the end, we insert after the last item.
       */
      result = tree.insertAfter(result, item, relativeTo.id);
      // } else {
      //   const relativeTo = siblingItems[action.index];
      //   /**
      //    * Otherwise we insert before the existing item in the given position.
      //    * This results in the new item being in that position.
      //    */
      //   result = tree.insertBefore(result, item, relativeTo.id);
    }

    return result;
  }

  return data;
};

function getChildItems(data: TreeItem[], targetId: string) {
  /**
   * An empty string is representing the root
   */
  if (targetId === "") {
    return data;
  }

  const targetItem = tree.find(data, targetId);
  invariant(targetItem);

  return targetItem.children;
}
